import { useEffect, useRef } from "react";

/**
 * One-shot celebration confetti in the app's red accent: two corner cannons,
 * shades derived from the live `--primary` token (so it follows the theme).
 * Self-contained canvas animation — no library, removes itself when done.
 */
export function ConfettiBurst({ onDone }: { onDone?: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      doneRef.current?.();
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Raw colour rather than `--primary`: `mix()` below expects a hex literal
    // and some semantic tokens resolve to color-mix expressions.
    const accent =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--nl-red")
        .trim() || "#ca2d37";
    const colors = [
      accent,
      mix(accent, [255, 255, 255], 0.3),
      mix(accent, [255, 255, 255], 0.55),
      mix(accent, [0, 0, 0], 0.25),
    ];

    type Particle = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      w: number;
      h: number;
      rot: number;
      vrot: number;
      color: string;
      life: number;
      maxLife: number;
    };

    const rand = (min: number, max: number) => min + Math.random() * (max - min);
    const spawn = (originX: number, baseAngle: number): Particle => {
      const angle = ((baseAngle + rand(-16, 16)) * Math.PI) / 180;
      const speed = rand(11, 21);
      return {
        x: originX,
        y: height + 8,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        w: rand(5, 9),
        h: rand(8, 14),
        rot: rand(0, Math.PI * 2),
        vrot: rand(-0.25, 0.25),
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 0,
        maxLife: rand(110, 170),
      };
    };

    const particles: Particle[] = [
      ...Array.from({ length: 80 }, () => spawn(width * 0.12, -68)),
      ...Array.from({ length: 80 }, () => spawn(width * 0.88, -112)),
    ];

    let frame = 0;
    let raf = 0;
    const tick = () => {
      ctx.clearRect(0, 0, width, height);
      let alive = 0;
      for (const p of particles) {
        if (p.life >= p.maxLife) continue;
        p.life += 1;
        p.vy += 0.34;
        p.vx *= 0.988;
        p.vy *= 0.988;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;
        if (p.y > height + 30) {
          p.life = p.maxLife;
          continue;
        }
        alive += 1;
        const fade = 1 - Math.max(0, (p.life / p.maxLife - 0.72) / 0.28);
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        // "Flip" around the long axis for a tumbling-paper look.
        ctx.scale(1, Math.sin(p.life * 0.15 + p.rot));
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      frame += 1;
      if (alive > 0 && frame < 260) {
        raf = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, width, height);
        doneRef.current?.();
      }
    };
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-50 h-full w-full"
    />
  );
}

/** Mixes a `#rrggbb` color toward a target RGB by t ∈ [0,1]. */
function mix(hex: string, target: [number, number, number], t: number): string {
  const raw = hex.replace("#", "");
  if (raw.length !== 6) return hex;
  const channel = (i: number) => {
    const v = parseInt(raw.slice(i * 2, i * 2 + 2), 16);
    return Math.round(v + (target[i] - v) * t);
  };
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
}
